import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, Bot } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const STEP_ACTIONS = [
  ["notify_email", "Notify customer (Email)"],
  ["notify_sms", "Notify customer (SMS)"],
  ["notify_whatsapp", "Notify customer (WhatsApp)"],
  ["reattempt", "Auto-request reattempt"],
  ["rto", "Auto-initiate RTO"],
];
const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminNdrRules() {
  const [rules, setRules] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/ndr/rules").then(({ data }) => setRules(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ name: "", enabled: true, priority: 1, steps: [{ after_hours: 0, action: "notify_whatsapp" }] });

  const save = async () => {
    try {
      await api.post("/admin/ndr/rules", editing);
      toast.success("Automation rule saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    await api.delete(`/admin/ndr/rules/${r.rule_id}`);
    toast.success("Rule deleted");
    load();
  };

  const toggle = async (r) => {
    await api.post("/admin/ndr/rules", { ...r, enabled: !r.enabled });
    load();
  };

  return (
    <div data-testid="admin-ndr-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">NDR Automation</h1>
          <p className="text-sm text-slate-500">NDR received → notify customer → confirm → reattempt. Runs every 15 min via cron.</p>
        </div>
        <button onClick={openNew} data-testid="ndr-rule-new-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue"><Plus className="h-4 w-4" /> New Rule</button>
      </div>

      <div className="mt-6 space-y-4" data-testid="ndr-rules-list">
        {(rules || []).map((r) => (
          <div key={r.rule_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ndr-rule-${r.rule_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Bot className={`h-5 w-5 ${r.enabled ? "text-shipany-cyan" : "text-slate-300"}`} />
                <div>
                  <p className="font-display font-bold text-shipany-navy">{r.name}</p>
                  <p className="text-xs text-slate-400">Priority P{r.priority}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(r)} data-testid={`ndr-rule-toggle-${r.rule_id}`} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${r.enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{r.enabled ? "ENABLED" : "DISABLED"}</button>
                <button onClick={() => setEditing(r)} data-testid={`ndr-rule-edit-${r.rule_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">Edit</button>
                <button onClick={() => remove(r)} data-testid={`ndr-rule-delete-${r.rule_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600">WHEN NDR received</span>
              {r.steps.map((s, i) => (
                <span key={i} className="flex items-center gap-2">
                  <span className="text-slate-300">→</span>
                  <span className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
                    {s.after_hours > 0 && <b className="mono text-shipany-blue">+{s.after_hours}h </b>}{STEP_ACTIONS.find(([k]) => k === s.action)?.[1] || s.action}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ))}
        {rules && rules.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No automation rules yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ndr-rule-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">{editing.rule_id ? "Edit Rule" : "New Automation Rule"}</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3.5">
              <input data-testid="ndr-rule-name" className={`${inputCls} w-full`} placeholder="Rule name *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <div className="rounded-xl bg-slate-50 p-3 text-xs font-bold text-slate-500">TRIGGER: NDR received on any shipment</div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Steps (in order)</p>
              {editing.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="mono w-5 text-xs text-slate-400">{i + 1}.</span>
                  <input type="number" min="0" step="1" data-testid={`ndr-step-hours-${i}`} className={`${inputCls} mono w-20`} value={s.after_hours}
                    onChange={(e) => { const steps = [...editing.steps]; steps[i] = { ...s, after_hours: e.target.value }; setEditing({ ...editing, steps }); }} />
                  <span className="text-xs text-slate-400">hrs →</span>
                  <select data-testid={`ndr-step-action-${i}`} className={`${inputCls} flex-1`} value={s.action}
                    onChange={(e) => { const steps = [...editing.steps]; steps[i] = { ...s, action: e.target.value }; setEditing({ ...editing, steps }); }}>
                    {STEP_ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <button onClick={() => setEditing({ ...editing, steps: editing.steps.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => setEditing({ ...editing, steps: [...editing.steps, { after_hours: 24, action: "notify_email" }] })} data-testid="ndr-step-add-btn"
                className="rounded-lg border border-dashed border-slate-300 px-4 py-2 text-xs font-semibold text-slate-500 hover:border-shipany-blue hover:text-shipany-blue">+ Add step</button>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <input type="checkbox" data-testid="ndr-rule-enabled" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
                Rule enabled
              </label>
            </div>
            <button onClick={save} data-testid="ndr-rule-save-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Save Rule</button>
          </div>
        </div>
      )}
    </div>
  );
}
